<script setup lang="ts">
import { api } from '@/api';
import type { RoleCatalogDto, UserCatalogDto } from '@/api/generator';
import { ref, onMounted, computed } from 'vue';
import { type SelectItemTypeObject } from '@/components/forms/types';
import VeeAutocomplete from '@/components/forms/Autocomplete/VeeAutocomplete.vue';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import { I18nKey } from '@/utils/typings';
import VeeCheckbox from '@/components/forms/Checkbox/VeeCheckbox.vue';
import { useFieldValue } from 'vee-validate';
import { useFormMode } from '@/components/forms/useFormMode';

const roles = ref<RoleCatalogDto[]>();
const users = ref<UserCatalogDto[]>();

const mode = useFormMode();

async function loadRoles() {
  const { result: usersResult } = await api.usersApi.getUsersCatalog();
  const { result: rolesResult } = await api.rolesApi.getRolesCatalog();
  if (usersResult && rolesResult) {
    roles.value = rolesResult;
    users.value = usersResult;
  }
}

const rolesOptions = computed(() =>
  roles.value?.map<SelectItemTypeObject<RoleCatalogDto>>((role) => ({
    title: role.name,
    value: role,
  })),
);

const userOptions = computed(() =>
  users.value?.map<SelectItemTypeObject<UserCatalogDto>>((user) => ({
    title: user.lastName,
    value: user,
  })),
);

const reviewRequired = useFieldValue<boolean | undefined>('reviewRequired');

onMounted(loadRoles);
</script>

<template>
  <div>
    <p
      v-if="mode === 'details' && !reviewRequired"
      class="text-body-1 text-primary-lighten-2 font-weight-semibold"
    >
      {{ $t('JobConfigurationNoReviewRequiredMsg') }}
    </p>

    <template v-else>
      <VeeAutocomplete
        :items="rolesOptions"
        solo
        chips
        clearable
        multiple
        :disabled="!reviewRequired"
        :label="$t('ReviewRoles')"
        name="reviewRoles"
        :placeholder="$t('Required')"
      >
        <template #itemTitle="{ value: role }">
          <IdentifierLabel
            :label="role.displayName ? $t(role.displayName as I18nKey) : role.name"
            :identifier="role.iuid"
          />
        </template>
      </VeeAutocomplete>

      <VeeAutocomplete
        :items="userOptions"
        class="gutterTop"
        solo
        chips
        clearable
        multiple
        :disabled="!reviewRequired"
        :label="$t('ReviewUsers')"
        name="reviewUsers"
        :placeholder="$t('Required')"
      >
        <template #itemTitle="item">
          <IdentifierLabel :label="item.value.fullName" :identifier="item.value.iuid" />
        </template>
      </VeeAutocomplete>

      <VeeCheckbox
        name="autoApprove"
        :label="$t('JobConfigurationAutoApprove')"
        mode="details"
        density="compact"
        hideDetails
        class="gutterTop"
      />
    </template>
  </div>
</template>
